import { ChevronDown, Text, Columns2, X, ChevronsUpDown, ChevronsDownUp, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { DeclarationSummary } from './DeclarationSummary';
import { useSelector, useDispatch } from 'react-redux';
import pdfIcon from "../../assets/pdf.png";
import PdfViewer from './PdfViewer';
import { scroller } from "react-scroll";
import { useLanguage, getReviewExtractedDataTranslation } from '../../hooks/useLanguage';
import { TemplateModal } from './TemplateModal';
import { setExtractedData, setEditableData, setSelectedTemplate, setDeclarationDirty } from '../../store/reducers';

export const ReviewExtractedData = () => {
  const dispatch = useDispatch();
  const cmrFile = useSelector((state) => state.documents.cmrFile);
  const exportFiles = useSelector((state) => state.documents.documents);
  const extractedData = useSelector((state) => state.documents.extractedData);
  const editableData = useSelector((state) => state.documents.editableData);

  const [files, setFiles] = useState([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [selectedOption, setSelectedOption] = useState('text');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [hasOpenedTemplateModal, setHasOpenedTemplateModal] = useState(false);

  const [isSpanish] = useLanguage();

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const selectedTemplate = useSelector((state) => state.documents.selectedTemplate);
  const templates = useSelector((state) => {
    const templates = state.templates;
    // Ensure templates is always an array
    return Array.isArray(templates) ? templates : [];
  });

  // Add/remove template class to body based on selected template
  useEffect(() => {
    // Remove all template classes
    const templateClasses = templates.map(t => `template-${t.id}`);
    document.body.classList.remove(...templateClasses);
    
    // Add class for selected template
    if (selectedTemplate) {
      document.body.classList.add(`template-${selectedTemplate.id}`);
    }

    // Cleanup: remove class when component unmounts or template changes
    return () => {
      if (selectedTemplate) {
        document.body.classList.remove(`template-${selectedTemplate.id}`);
      }
    };
  }, [selectedTemplate, templates]);

  // Open template modal automatically when extractedData is first set from API
  useEffect(() => {
    // Only open modal if:
    // 1. extractedData exists and has content
    // 2. No template is currently selected
    // 3. Modal is not already open
    // 4. We haven't already opened it for this extraction session
    if (extractedData && 
        (Array.isArray(extractedData) ? extractedData.length > 0 : true) &&
        !selectedTemplate && 
        !isTemplateModalOpen &&
        !hasOpenedTemplateModal) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        setIsTemplateModalOpen(true);
        setHasOpenedTemplateModal(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [extractedData, selectedTemplate, isTemplateModalOpen, hasOpenedTemplateModal]);

  // Reset the flag when extractedData is cleared (new extraction session)
  useEffect(() => {
    if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
      setHasOpenedTemplateModal(false);
    }
  }, [extractedData]);

  useEffect(() => {
    const validFiles = [cmrFile, ...exportFiles].filter(Boolean);
    setFiles(validFiles);

    if (validFiles.length > 0 && !selectedFile) {
      handleFileClick(validFiles[0]);
    }
  }, [cmrFile, exportFiles]);

  useEffect(() => {
    return () => {
      if (selectedFileUrl) {
        URL.revokeObjectURL(selectedFileUrl);
      }
    };
  }, [selectedFileUrl]);

  const closeModal = () => {
    setSelectedOption('text');
    scroller.scrollTo('review-extracted-data', {
      duration: 1500,
      delay: 20,
      offset: -24,
      smooth: 'easeInOutQuint',
    });
  };

  const handleFileClick = (file) => {
    if (selectedFileUrl) {
      URL.revokeObjectURL(selectedFileUrl);
    }

    const fileUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedFileUrl(fileUrl);
  };

  const handleTemplateSelect = (template) => {
    // Store the current template as previous before applying new one
    if (selectedTemplate) {
      sessionStorage.setItem('previousSelectedTemplate', JSON.stringify(selectedTemplate));
    } else {
      // If no template was selected before, store null to indicate no previous template
      sessionStorage.setItem('previousSelectedTemplate', JSON.stringify(null));
    }
    
    dispatch(setSelectedTemplate(template));
    
    // Apply template data to editableData (preserves expediente values)
    const currentData = editableData || extractedData;
    
    if (currentData && template?.data) {
      // Get original data from sessionStorage (before any templates were applied)
      let originalData = null;
      try {
        const stored = sessionStorage.getItem('originalExtractedData');
        if (stored) {
          originalData = JSON.parse(stored);
        }
      } catch (e) {
        console.error('Error parsing originalExtractedData:', e);
      }
      
      // Handle both array and single object cases
      const dataArray = Array.isArray(currentData) ? currentData : [currentData];
      const originalDataArray = originalData 
        ? (Array.isArray(originalData) ? originalData : [originalData])
        : null;
      
      const updatedData = dataArray.map((declaration, index) => {
            // Map template data to declaration structure, preserving expediente
            const updatedDeclaration = { ...declaration };
            
            // Find the original declaration by expediente or by index if expediente doesn't match
            let originalDeclaration = null;
            if (originalDataArray) {
              // First try to match by expediente
              originalDeclaration = originalDataArray.find(d => 
                d.expediente === declaration.expediente
              );
              // If no match by expediente, try by index
              if (!originalDeclaration && originalDataArray[index]) {
                originalDeclaration = originalDataArray[index];
              }
            }
            
            // Define all template-applicable fields
            const templateFields = {
              regimen_solicitud: template.data.regimen_solicitud,
              declaracion_1: template.data.declaracion_1,
              declaracion_2: template.data.declaracion_2,
              naturaleza_transaccion: template.data.naturaleza_transaccion,
              contacto_telefono: template.data.telefono,
              e_mail: template.data.contacto,
              marcas: template.data.marcas,
              medio_transporte_frontera: template.data.medio_transporte,
              aduana_despacho: template.data.aduana_despacho,
              aduana_salida: template.data.aduana_salida,
              pais_exportacion: template.data.pais_exportacion,
              provincia: template.data.provincia,
              localizacion: template.data.localizacion_mercancia,
            };
            
            // Apply template fields: set if exists in template, restore to original if not
            Object.keys(templateFields).forEach((key) => {
              if (templateFields[key] !== undefined) {
                // Field exists in template - apply it
                updatedDeclaration[key] = templateFields[key];
              } else {
                // Field doesn't exist in template - restore to original value or clear
                const templateSpecificFields = [
                  'regimen_solicitud', 'declaracion_1', 'declaracion_2', 
                  'naturaleza_transaccion', 'contacto_telefono', 'e_mail',
                  'marcas', 'medio_transporte_frontera', 'aduana_despacho',
                  'aduana_salida', 'pais_exportacion', 'provincia', 'localizacion'
                ];
                if (templateSpecificFields.includes(key)) {
                  // Restore to original value from before templates were applied
                  if (originalDeclaration && originalDeclaration[key] !== undefined) {
                    updatedDeclaration[key] = originalDeclaration[key];
                  } else {
                    // Clear if not in original data
                    updatedDeclaration[key] = '';
                  }
                }
              }
            });
            
            // Handle documentos: add to partidas level, not declaration level
            // Map template document tipos to codigo (tipo) based on DocumentosModal:
            // '1' = Autorizaciones, '2' = Documentos Suplementarios, '3' = Referencias Adicionales, '4' = Documentos de Transporte
            if (template.data.documentos && Array.isArray(template.data.documentos) && template.data.documentos.length > 0) {
              // Template has documentos - apply them to all partidas
              const templateDocumentos = template.data.documentos.map((doc) => {
                let tipoCodigo = 2; // Default: Documentos Suplementarios
                
                // Map document tipo to codigo (tipo field)
                if (doc.tipo === 'N730') {
                  tipoCodigo = 4; // Documentos de Transporte
                } else if (doc.tipo === 'Y024') {
                  tipoCodigo = 3; // Referencias Adicionales
                } else if (doc.tipo === 'I5007') {
                  tipoCodigo = 2; // Documentos Suplementarios
                } else {
                  // Other document types (N380, N003, 1217, 1009, etc.) - Documentos Suplementarios
                  tipoCodigo = 2;
                }
                
                return {
                  codigo_documento: doc.tipo,
                  tipo: tipoCodigo,
                  numero: '',
                  fecha: null,
                };
              });
              
              // Apply documentos to all partidas in this declaration
              if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
                updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                  ...partida,
                  documentos: [...templateDocumentos], // Overwrite with template documentos
                }));
              }
            } else {
              // Template has no documentos - restore to original documentos from before any templates were applied
              if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
                if (originalDeclaration && originalDeclaration.partidas && Array.isArray(originalDeclaration.partidas)) {
                  // Restore each partida's documentos from original
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => ({
                    ...partida,
                    documentos: originalDeclaration.partidas[partidaIndex]?.documentos 
                      ? [...originalDeclaration.partidas[partidaIndex].documentos]
                      : [],
                  }));
                } else {
                  // No original data, clear all documentos
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                    ...partida,
                    documentos: [],
                  }));
                }
              }
            }
            
            return updatedDeclaration;
          });
      
      // Convert back to original format if it wasn't an array
      const finalData = Array.isArray(currentData) 
        ? (updatedData.length > 0 ? updatedData : currentData)
        : (updatedData[0] || updatedData || currentData);
      
      // Safety check: ensure we have valid data before updating
      if (finalData && (Array.isArray(finalData) ? finalData.length > 0 : true)) {
        // Update editableData first (preserves expediente and doesn't reset isDirty)
        dispatch(setEditableData(finalData));
        
        // Update extractedData (will preserve expediente due to reducer fix)
        dispatch(setExtractedData(finalData));
        
        // Set isDirty to true since we made changes
        dispatch(setDeclarationDirty(true));
        setIsDirty(true);
      } else {
        console.warn('Template application resulted in empty data, skipping update');
      }
    }
  };

  return (
    <div className="mt-8" id='review-extracted-data'>
      {selectedOption === 'split' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-999 py-2 px-3 h-screen flex flex-col gap-2">
          <div
            className="shadow-md flex absolute z-[999] bottom-5 items-center gap-2 p-2 px-4 rounded-full right-7 bg-red-50 cursor-pointer"
            onClick={closeModal}
          >
            <X className="size-4 mt-[2px] stroke-red-500" />
            <p className="text-sm text-red-500">{getReviewExtractedDataTranslation('close', isSpanish)}</p>
          </div>
          
          <div className="flex items-center justify-center gap-2 flex-1">
            <div className="bg-white p-4 shadow-lg min-w-[50%] min-h-[100%] overflow-y-auto flex-1 flex flex-col rounded-lg">
              <div className='flex items-center justify-between gap-2 mb-1 flex-wrap'>
              <h2 className="text-lg font-bold truncate" title='Uploaded Files'>
                {getReviewExtractedDataTranslation('uploadedFiles', isSpanish)} ({files.length})
              </h2>
            <div className='relative text-xs'>
                <button className={` w-fit flex gap-2 justify-between items-center`}  onClick={() => setDropdownOpen(!dropdownOpen)}>
                    {selectedFile ? 
                    <div
                    className={`truncate border-container px-2 rounded-full cursor-pointer`}
                  >
                    <div className="p-2 px-1 flex gap-2 items-center">
                      <div className="flex gap-1 items-center">
                        <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                        <p className="truncate max-w-xs">{selectedFile.name}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
                    </div>
                  </div>:  <div
                    className={`truncate border-container px-2 rounded-full cursor-pointer`}
                  >
                    <div className="p-2 px-1 flex gap-2 items-center">
                      <div className="flex gap-1 items-center">
                        <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                        <p className="truncate max-w-xs">{getReviewExtractedDataTranslation('noPdfFileSelected', isSpanish)}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
                    </div>
                  </div>}
                    
                    
                </button>
                {dropdownOpen && (
                  <div className='right-0 bg-white shadow-md p-2 border-container rounded-md absolute mt-1  max-h-96 overflow-auto'>
                     {files.length > 0 ? (
                      <div className="flex text-xs gap-1.5 flex-col py-1">
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className={` truncate border-container px-2 rounded-full cursor-pointer ${
                              selectedFile === file ? 'bg-secondary text-white !border-secondary' : ''
                            }`}
                            onClick={() => handleFileClick(file)}
                          >
                            <div className="p-2 px-1">
                              <div className="flex gap-1 items-center">
                                <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                                <p className="truncate max-w-xs">{file.name}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-100 p-4 rounded text-center text-gray-500">
                        {getReviewExtractedDataTranslation('noFilesAvailable', isSpanish)}
                      </div>
                    )}

                    </div>
                )}
            </div>
            </div>

              <div className="pdf-viewer flex flex-1 w-full rounded-lg overflow-hidden mt-2">
                {selectedFileUrl ? (
                  <PdfViewer 
                    selectedFileUrl={selectedFileUrl} 
                    fileName={selectedFile?.name || ""}
                  />
                ) : (
                  <div className="min-w-full bg-gray-100 p-8 rounded-lg flex justify-center items-center text-gray-500">
                    {getReviewExtractedDataTranslation('selectAFileToView', isSpanish)}
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white shadow-lg min-w-[50%] min-h-[100%] max-h-[94vh] overflow-hidden flex-1 rounded-lg">
              <div className="p-4 pt-0 overflow-auto split-declaration-height pb-16">
                  <DeclarationSummary
                  selectedOption={selectedOption}
                    jsonData={extractedData}
                    isSummaryExpanded={isSummaryExpanded}
                    setIsSummaryExpanded={setIsSummaryExpanded}
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className='flex gap-1 items-center mb-2 justify-between'>
        <h1 className='text-xl'>{getReviewExtractedDataTranslation('extractedData', isSpanish)}</h1>
      
      <div className="flex gap-1 items-center">
        <button
          className={`flex items-center gap-1 py-2 px-2${
            selectedOption === 'split' ? 'bg-secondary' : ''
          } rounded-full`}
          title="Expand"
          onClick={() => {
            setIsSummaryExpanded(!isSummaryExpanded);
          }}
        >
            {isSummaryExpanded ? <ChevronsDownUp className="size-4" /> : <ChevronsUpDown className="size-4" />}
          
          <p className='text-sm'>{isSummaryExpanded ? getReviewExtractedDataTranslation('collapse', isSpanish) : getReviewExtractedDataTranslation('expand', isSpanish)}</p>
          
        </button>
        <button
          className={`flex items-center gap-2 py-2 bg-secondary px-4${
            selectedOption === 'split' ? 'bg-secondary' : ''
          } rounded-full`}
          title="Split View"
          onClick={() => {
            setSelectedOption('split');
            setIsSummaryExpanded(true);
          }}
        >
            
          <Columns2 className="size-5" />
          <p className='text-sm'>{getReviewExtractedDataTranslation('splitView', isSpanish)}</p>
          
        </button>
        <button
          className="flex items-center gap-4 py-2 px-4 rounded-full template-buttona bg-primary"
          title="Template"
          onClick={() => {
            setIsTemplateModalOpen(true);
          }}
        >
          <div className='flex items-center gap-2'>
          <FileText className="size-5" />
          <p className='text-sm uppercase'>{selectedTemplate ? selectedTemplate.title : getReviewExtractedDataTranslation('selectTemplate', isSpanish)}</p>
          </div>
          <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
        </button>
      </div>
      </div>
      <div className="border-container p-4 pt-0 rounded-xl">
          <DeclarationSummary
            jsonData={extractedData}
            isSummaryExpanded={isSummaryExpanded}
            setIsSummaryExpanded={setIsSummaryExpanded}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
          />
      </div>
      <TemplateModal
        templates={templates}
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelect={handleTemplateSelect}
        selectedTemplate={selectedTemplate}
        title={getReviewExtractedDataTranslation('selectTemplateTitle', isSpanish)}
        isSpanish={isSpanish}
      />
    </div>
  );
};